import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { format } from "date-fns";
import { ArrowLeft, ImageIcon, Instagram, Minus, Phone, Plus, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, productImage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ORDER_STATUS_META, fmtIDR } from "@/lib/constants";
import { NumInput } from "@/components/NumInput";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import OrderFinance from "@/components/OrderFinance";
import ProductForm from "@/components/ProductForm";
import ProductCombobox from "@/components/ProductCombobox";
import { FulfillmentToggle, ReadyCountLine, ReadyItemBadge } from "@/components/ReadyItems";

const CAN_MANAGE = ["owner", "admin_input"];
const ORDER_EVENT_LABELS = {
  order_created: "Order created",
  items_added: "Items added to running order",
  item_cancelled: "Item cancelled",
  order_cancelled: "Order cancelled",
};

export default function OrderDetail() {
  const { orderId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [products, setProducts] = useState([]);
  const [addPid, setAddPid] = useState("");
  const [addQty, setAddQty] = useState(1);
  const [addPrice, setAddPrice] = useState("");
  const [addVariant, setAddVariant] = useState("");
  const [addType, setAddType] = useState("PO");
  const [confirmCancelItem, setConfirmCancelItem] = useState(null);
  const [confirmCancelOrder, setConfirmCancelOrder] = useState(false);
  const [busy, setBusy] = useState(false);
  const [newProdOpen, setNewProdOpen] = useState(false);

  const canManage = CAN_MANAGE.includes(user.role);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/orders/${orderId}`);
      setOrder(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, [orderId]);

  useEffect(() => {
    load();
  }, [load]);

  const loadProducts = useCallback(async () => {
    if (!order?.po_batch_id) return;
    try {
      const { data } = await api.get(`/batches/${order.po_batch_id}/products`);
      setProducts(data.products || []);
    } catch {}
  }, [order?.po_batch_id]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const onProductSaved = (saved, typedQty) => {
    loadProducts();
    if (saved?.id) {
      setAddPid(saved.id);
      if (saved.selling_price != null) setAddPrice(String(saved.selling_price));
      setAddQty(typedQty > 0 ? typedQty : 1);
    }
  };

  if (!order) {
    return (
      <div className="grid place-items-center py-24" data-testid="order-loading">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  const meta = ORDER_STATUS_META[order.status] || ORDER_STATUS_META.active;
  const batchMissing = order.batch_missing === true || order.batch_status === "missing";
  const batchOpen = order.batch_status !== "closed" && !batchMissing;
  const canAddItems = canManage && order.status === "active" && batchOpen;
  const sortedItems = [...(order.items || [])].sort((a, b) => a.seq - b.seq);
  const activeItems = sortedItems.filter((i) => i.status === "active");
  const totalQty = activeItems.reduce((s, i) => s + i.quantity, 0);
  const totalValue = activeItems.reduce((s, i) => s + (i.selling_price || 0) * i.quantity, 0);

  const addItem = async () => {
    if (!addPid || addQty < 1) return;
    setBusy(true);
    try {
      await api.post(`/orders/${orderId}/items`, {
        items: [{ product_id: addPid, quantity: addQty, selling_price: addPrice === "" ? null : Number(addPrice), variant: addVariant.trim() || null, fulfillment_type: addType }],
      });
      toast.success("Item added to running order");
      setAddPid("");
      setAddType("PO");
      setAddQty(1);
      setAddPrice("");
      setAddVariant("");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const cancelItem = async (itemId) => {
    if (confirmCancelItem !== itemId) {
      setConfirmCancelItem(itemId);
      return;
    }
    setBusy(true);
    try {
      await api.post(`/orders/${orderId}/items/${itemId}/cancel`);
      toast.success("Item cancelled (history preserved)");
      setConfirmCancelItem(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const cancelOrder = async () => {
    if (!confirmCancelOrder) {
      setConfirmCancelOrder(true);
      return;
    }
    setBusy(true);
    try {
      await api.post(`/orders/${orderId}/cancel`);
      toast.success("Order cancelled (history preserved)");
      setConfirmCancelOrder(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="order-detail-page">
      <button
        data-testid="back-to-orders"
        onClick={() => navigate("/orders")}
        className="mb-3 flex h-10 items-center gap-1 text-sm font-semibold text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Customer Orders
      </button>

      {batchMissing && (
        <div data-testid="order-batch-missing-banner" className="mb-4 rounded-md border border-amber-300 bg-amber-50 p-4">
          <p className="font-heading text-sm font-extrabold text-amber-900">PO Batch no longer exists</p>
          <p className="mt-1 text-xs text-amber-800">
            This order references the PO Batch <b>{order.batch_name || "(unknown)"}</b>, which has been deleted. New item entry is
            locked for this order. Nothing was reassigned to another batch, and all invoices, payments, customer credits and
            audit history for this order are preserved. To continue selling, create a new order in a valid active batch.
          </p>
          {user.role === "owner" && (
            <button
              type="button"
              data-testid="order-batch-missing-review-link"
              className="mt-2 text-xs font-bold text-amber-900 underline"
              onClick={() => navigate("/owner-review")}
            >
              Owner review of affected orders →
            </button>
          )}
        </div>
      )}

      <div className="mb-4 rounded-md border bg-card p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h1 className="font-heading text-2xl font-extrabold tracking-tight" data-testid="order-customer-name">{order.customer_name}</h1>
            <ReadyCountLine po={order.po_line_count} ready={order.ready_line_count} testid="order-type-count" />
            <p className="mt-0.5 text-sm text-muted-foreground">
              {batchMissing ? (
              <span className="font-semibold text-amber-700">{order.batch_name || "(deleted batch)"} — deleted</span>
            ) : (
              <button className="font-semibold text-primary" onClick={() => navigate(`/batches/${order.po_batch_id}`)}>{order.batch_name}</button>
            )}
              {" · "}#{order.id.slice(0, 8)} · created {format(new Date(order.created_at), "d MMM yyyy, HH:mm")} by {order.created_by_name}
            </p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {order.customer_phone && (
                <span className="inline-flex items-center gap-1 rounded-md border bg-[#EDE4DE] px-2 py-0.5 text-xs font-semibold text-stone-600">
                  <Phone className="h-3 w-3" /> {order.customer_phone}
                </span>
              )}
              {order.customer_instagram && (
                <span className="inline-flex items-center gap-1 rounded-md border bg-[#EDE4DE] px-2 py-0.5 text-xs font-semibold text-stone-600">
                  <Instagram className="h-3 w-3" /> @{order.customer_instagram}
                </span>
              )}
              {order.customer_source && (
                <span className="rounded-md border bg-[#EDE4DE] px-2 py-0.5 text-xs font-semibold text-stone-600">{order.customer_source}</span>
              )}
              {order.sales_admin && (
                <span className="rounded-md border bg-[#EDE4DE] px-2 py-0.5 text-xs font-semibold text-stone-600">Admin: {order.sales_admin}</span>
              )}
            </div>
          </div>
          <span data-testid="order-status-badge" className={`shrink-0 rounded-md border px-2 py-0.5 text-xs font-bold ${meta.badge}`}>{meta.label}</span>
        </div>
        {order.notes && <p className="mt-3 rounded-md border bg-accent/40 px-3 py-2 text-xs text-stone-600" data-testid="order-note"><b>Note:</b> {order.notes}</p>}
        <div className="mt-3 flex gap-2" data-testid="order-totals">
          <span className="rounded-md border bg-[#EDE4DE] px-2.5 py-1 text-xs font-semibold">{totalQty} units</span>
          {user.role !== "viewer" && (
            <span className="rounded-md border bg-[#EDE4DE] px-2.5 py-1 text-xs font-semibold" data-testid="order-total-value">{fmtIDR(totalValue)}</span>
          )}
        </div>
      </div>

      <h2 className="mb-2 font-heading text-sm font-bold uppercase tracking-wide text-muted-foreground">Order Items (chronological)</h2>
      <div className="space-y-2" data-testid="order-items">
        {sortedItems.map((it) => (
          <div key={it.id} data-testid={`order-item-${it.id}`} className={`rounded-md border bg-card p-3 ${it.status === "cancelled" ? "opacity-60" : ""}`}>
            <div className="flex items-center gap-3">
              {productImage(it) ? (
                <img src={productImage(it)} alt={it.product_name} className="h-12 w-12 shrink-0 rounded-md border bg-stone-100 object-cover" />
              ) : (
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md border bg-stone-100">
                  <ImageIcon className="h-5 w-5 text-stone-300" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="min-w-0 truncate text-sm font-bold">{it.product_name}</p>
                  {it.variant && <span className="shrink-0 rounded-md border bg-accent px-1.5 py-0.5 text-[10px] font-bold">{it.variant}</span>}
                </div>
                <ReadyItemBadge item={it} />
                <p className="text-xs text-muted-foreground">
                  Qty {it.quantity}
                  {it.selling_price != null && ` · ${fmtIDR(it.selling_price)}`}
                  {it.notes && ` · ${it.notes}`}
                </p>
                {(it.allocated_qty || 0) > 0 && (
                  <p data-testid={`item-allocated-${it.id}`} className="mt-0.5 text-[11px] font-bold text-emerald-700">
                    Allocated {it.allocated_qty}/{it.quantity} from approved stock
                  </p>
                )}
                <p className="mt-0.5 text-[11px] text-muted-foreground">
                  <span data-testid={`item-seq-${it.id}`} className="font-bold text-primary">seq #{it.seq}</span>
                  {" · "}added {format(new Date(it.created_at), "d MMM yyyy, HH:mm")} by {it.created_by_name}
                  {it.status === "cancelled" && ` · cancelled ${format(new Date(it.cancelled_at), "d MMM, HH:mm")} by ${it.cancelled_by_name}`}
                </p>
                {it.status === "cancelled" && (it.released_qty || 0) > 0 && (
                  <p data-testid={`item-released-${it.id}`} className="mt-0.5 text-[11px] font-bold text-amber-700">
                    Released {it.released_qty} allocated unit{it.released_qty === 1 ? "" : "s"} back to Available to Allocate
                  </p>
                )}
              </div>
              {it.status === "cancelled" ? (
                <span className="shrink-0 rounded-md border border-red-200 bg-red-50 px-2 py-0.5 text-[10px] font-bold text-red-600">CANCELLED</span>
              ) : (
                canManage && order.status === "active" && (
                  <Button
                    variant={confirmCancelItem === it.id ? "destructive" : "outline"}
                    size="sm"
                    data-testid={`item-cancel-${it.id}`}
                    className="h-9 shrink-0"
                    onClick={() => cancelItem(it.id)}
                    disabled={busy}
                  >
                    {confirmCancelItem === it.id ? "Confirm" : "Cancel"}
                  </Button>
                )
              )}
            </div>
          </div>
        ))}
      </div>

      {canAddItems && (
        <div className="mt-4 space-y-3 rounded-md border bg-accent/30 p-4" data-testid="add-item-block">
          <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Add item to this running order</p>
          <ProductCombobox
            products={products}
            testid="add-item-combobox"
            onPick={(p) => { setAddPid(p.id); if (p.selling_price != null) setAddPrice(String(p.selling_price)); }}
          />
          {addPid && (
            <div className="flex items-center justify-between gap-2 rounded-md border bg-card px-3 py-2 text-xs font-semibold" data-testid="add-item-selected">
              <span className="min-w-0 truncate">{products.find((p) => p.id === addPid)?.name}</span>
              <FulfillmentToggle value={addType} onChange={setAddType} testid="add-item-type" />
            </div>
          )}
          <Button variant="outline" data-testid="add-item-new-product" className="h-11 w-full" onClick={() => setNewProdOpen(true)}>
            <Plus className="h-4 w-4" /> New Product (not in list)
          </Button>
          {addPid && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1.5 rounded-md border bg-white p-1">
                  <button type="button" data-testid="add-item-qty-minus" aria-label="Decrease" className="flex h-9 w-9 items-center justify-center rounded-md bg-accent" onClick={() => setAddQty(Math.max(1, addQty - 1))}>
                    <Minus className="h-4 w-4" />
                  </button>
                  <span data-testid="add-item-qty-value" className="w-8 text-center font-heading text-base font-extrabold">{addQty.toLocaleString("id-ID")}</span>
                  <button type="button" data-testid="add-item-qty-plus" aria-label="Increase" className="flex h-9 w-9 items-center justify-center rounded-md bg-accent" onClick={() => setAddQty(addQty + 1)}>
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
                <NumInput data-testid="add-item-price" mode="money" className="h-10 flex-1" placeholder="Selling price (IDR)" value={addPrice} onValue={setAddPrice} />
              </div>
              <Input data-testid="add-item-variant" className="h-10" placeholder="Variant (optional)" value={addVariant} onChange={(e) => setAddVariant(e.target.value)} />
              <Button data-testid="add-item-save" className="h-11 w-full" onClick={addItem} disabled={busy}>
                Add Item
              </Button>
            </div>
          )}
        </div>
      )}

      {canManage && order.status === "active" && (
        <Button
          variant={confirmCancelOrder ? "destructive" : "outline"}
          data-testid="cancel-order-button"
          className="mt-4 h-11 w-full"
          onClick={cancelOrder}
          disabled={busy}
        >
          {confirmCancelOrder ? "Confirm — cancel this entire order" : "Cancel Order"}
        </Button>
      )}

      {!batchMissing && (
        <ProductForm forOrder batchId={order.po_batch_id} product={null} open={newProdOpen} onOpenChange={setNewProdOpen} onSaved={onProductSaved} />
      )}

      {["owner", "admin_input", "admin"].includes(user.role) && <OrderFinance order={order} />}

      {order.audit?.length > 0 && (
        <div className="mt-5" data-testid="order-audit">
          <h2 className="mb-2 font-heading text-sm font-bold uppercase tracking-wide text-muted-foreground">Order History</h2>
          <div className="space-y-1.5">
            {[...order.audit].reverse().map((e, i) => (
              <div key={i} className="rounded-md border bg-card p-3">
                <p className="text-xs font-semibold">{ORDER_EVENT_LABELS[e.event] || e.event}</p>
                <p className="text-[11px] text-muted-foreground">
                  {e.by_name} · {format(new Date(e.at), "d MMM yyyy, HH:mm")}
                  {e.details?.items && ` · ${e.details.items.map((d) => `${d.product_name} x${d.quantity} (seq #${d.seq})`).join(", ")}`}
                  {e.details?.product_name && ` · ${e.details.product_name} x${e.details.quantity} (seq #${e.details.seq})`}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
